import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints, urlClientFinderOrgCompaniesUpload } from "../config/api";

export const TEMPLATE_COLUMNS = [
  { name: "Company Name", required: true, description: "The company's name", example: "Apollo Hospitals" },
  { name: "What they do", required: false, description: "One line on what the company does", example: "Multi-specialty hospital chain" },
  { name: "Website", required: false, description: "Its website or domain", example: "apollohospitals.com" },
  { name: "Why would they buy", required: false, description: "Why it might need what you sell", example: "Large wards that need cooling" },
  { name: "Country", required: false, description: "Where it's based", example: "India" },
  { name: "LinkedIn Page", required: false, description: "Its LinkedIn company page URL", example: "https://www.linkedin.com/company/apollo-hospitals" },
];

const ACCEPTED_EXTENSIONS = [".xlsx", ".csv"];

const csvCell = (value) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

export const buildTemplateCsv = () =>
  [TEMPLATE_COLUMNS.map((c) => c.name), TEMPLATE_COLUMNS.map((c) => c.example)]
    .map((row) => row.map(csvCell).join(","))
    .join("\n") + "\n";

const downloadTemplate = () => {
  const blob = new Blob([buildTemplateCsv()], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "client_finder_companies_template.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const isAcceptedFile = (file) =>
  ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

/** Picks the numeric counts out of the upload response, whatever their names are. */
const summarizeResponse = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];
  return Object.entries(data)
    .filter(([, value]) => typeof value === "number")
    .map(([key, value]) => ({ label: key.replace(/_/g, " "), value }));
};

const ClientFinder = ({ onClose }) => {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await axios.get(endpoints.orgs, {
          headers: { ...getAuthHeaders(), Accept: "*/*", "Content-Type": "application/json" },
        });
        setOrgs(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching orgs", error);
        setErrorMessage("Failed to fetch organizations.");
      }
    };
    fetchOrgs();
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0] || null;
    setResult(null);
    setSuccessMessage("");
    if (file && !isAcceptedFile(file)) {
      setSelectedFile(null);
      setErrorMessage("Please choose an .xlsx or .csv file.");
      event.target.value = "";
      return;
    }
    setErrorMessage("");
    setSelectedFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile || !selectedOrgId) {
      setErrorMessage("Please select an organization and a file.");
      return;
    }

    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setErrorMessage("Please log in to upload companies.");
      return;
    }

    setLoading(true);
    setResult(null);
    setSuccessMessage("");
    setErrorMessage("");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      // Let axios set the multipart boundary for FormData.
      const response = await axios.post(urlClientFinderOrgCompaniesUpload(selectedOrgId), formData, {
        headers: { ...authHeaders },
      });
      const orgName = orgs.find((o) => String(o.id) === String(selectedOrgId))?.name || "the organization";
      setSuccessMessage(
        `Uploaded "${selectedFile.name}" to ${orgName}'s company list. Companies are now being prepared in the background.`
      );
      setResult(response.data ?? null);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error uploading Client Finder companies", error?.response);
      const detail = error?.response?.data?.detail;
      setErrorMessage(
        typeof detail === "string" && detail.trim()
          ? detail
          : getErrorMessage(error, "Failed to upload companies. Please contact the team to fix.")
      );
    } finally {
      setLoading(false);
    }
  };

  const headingStyle = { color: "#3730a3", fontSize: "28px", fontWeight: "bold", marginBottom: "8px", lineHeight: "1.2" };
  const subheadingStyle = { color: "#3730a3", fontSize: "18px", fontWeight: "700", margin: "0 0 12px" };
  const mutedStyle = { color: "#4b5563", fontSize: "14px", lineHeight: "1.6", margin: "0 0 24px" };
  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#1f2937",
    outline: "none",
  };
  const labelStyle = { display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#3730a3" };
  const buttonStyle = {
    padding: "12px 24px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  };
  const secondaryButtonStyle = { ...buttonStyle, backgroundColor: "#ffffff", color: "#3730a3", border: "1px solid #3730a3" };
  const sectionStyle = { border: "1px solid #e0e7ff", borderRadius: "10px", padding: "20px", marginBottom: "24px", backgroundColor: "#f8faff" };
  const formGroupStyle = { marginBottom: "20px" };
  const successMessageStyle = { color: "#047857", marginBottom: "16px", padding: "12px", backgroundColor: "#d1fae5", borderRadius: "6px", border: "1px solid #10b981" };
  const errorMessageStyle = { color: "#ef4444", marginBottom: "16px", padding: "12px", backgroundColor: "#fee2e2", borderRadius: "6px", border: "1px solid #ef4444" };
  const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "13px", backgroundColor: "#ffffff" };
  const cellStyle = { border: "1px solid #e0e7ff", padding: "8px 10px", textAlign: "left", verticalAlign: "top", color: "#1f2937" };
  const headCellStyle = { ...cellStyle, backgroundColor: "#eef2ff", color: "#3730a3", fontWeight: "700" };
  const listStyle = { margin: "0 0 12px", paddingLeft: "20px", color: "#374151", fontSize: "14px", lineHeight: "1.7" };

  const disabled = loading || !selectedFile || !selectedOrgId;
  const counts = summarizeResponse(result);

  return (
    <div>
      {onClose && (
        <button
          style={{
            height: "40px",
            padding: "0 16px",
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#3730a3",
            color: "#ffffff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
            marginBottom: "20px",
          }}
          onClick={onClose}
        >
          Close
        </button>
      )}
      <h2 style={headingStyle}>Client Finder</h2>
      <p style={mutedStyle}>
        Upload a company list to an organization's shared Client Finder list. Uploading again updates the same
        companies and never duplicates them.
      </p>

      <section style={sectionStyle}>
        <h3 style={subheadingStyle}>Upload organization companies</h3>
        {successMessage && <div style={successMessageStyle}>{successMessage}</div>}
        {errorMessage && <div style={errorMessageStyle}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div style={formGroupStyle}>
            <label htmlFor="client-finder-org-select" style={labelStyle}>
              Select Organization
            </label>
            <select
              id="client-finder-org-select"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              required
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              <option value="">Select an organization</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="client-finder-file-input" style={labelStyle}>
              Company list (.xlsx or .csv)
            </label>
            <input
              id="client-finder-file-input"
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              required
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              type="submit"
              disabled={disabled}
              style={{ ...buttonStyle, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
            >
              {loading ? "Uploading..." : "Upload Companies"}
            </button>
            <button type="button" onClick={downloadTemplate} style={secondaryButtonStyle}>
              Download Template
            </button>
          </div>
        </form>

        {result && (
          <div style={{ marginTop: "20px" }}>
            {counts.length > 0 && (
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
                {counts.map(({ label, value }) => (
                  <div
                    key={label}
                    style={{ padding: "10px 14px", border: "1px solid #c7d2fe", borderRadius: "8px", backgroundColor: "#ffffff", minWidth: "110px" }}
                  >
                    <div style={{ fontSize: "20px", fontWeight: "700", color: "#3730a3" }}>{value}</div>
                    <div style={{ fontSize: "12px", color: "#4b5563", textTransform: "capitalize" }}>{label}</div>
                  </div>
                ))}
              </div>
            )}
            <details>
              <summary style={{ cursor: "pointer", color: "#3730a3", fontWeight: "600", fontSize: "14px" }}>
                Full response
              </summary>
              <pre
                style={{ marginTop: "8px", padding: "12px", backgroundColor: "#0f172a", color: "#e2e8f0", borderRadius: "6px", fontSize: "12px", overflowX: "auto", maxHeight: "320px" }}
              >
                {JSON.stringify(result, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </section>

      <section style={sectionStyle}>
        <h3 style={subheadingStyle}>Company list template</h3>
        <ul style={listStyle}>
          <li><strong>Format:</strong> .xlsx or .csv. Only the first sheet is read.</li>
          <li><strong>Rows:</strong> no limit.</li>
          <li><strong>Headers:</strong> matched by name, in any order; upper or lower case doesn't matter.</li>
          <li>
            <strong>Required:</strong> only <strong>Company Name</strong>. Rows without a company name are skipped and
            reported back.
          </li>
        </ul>

        <div style={{ overflowX: "auto", marginBottom: "16px" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headCellStyle}>Column</th>
                <th style={headCellStyle}>Required</th>
                <th style={headCellStyle}>What to put in it</th>
                <th style={headCellStyle}>Example</th>
              </tr>
            </thead>
            <tbody>
              {TEMPLATE_COLUMNS.map((col) => (
                <tr key={col.name}>
                  <td style={{ ...cellStyle, fontWeight: col.required ? "700" : "400" }}>{col.name}</td>
                  <td style={cellStyle}>{col.required ? "Yes" : "No"}</td>
                  <td style={cellStyle}>{col.description}</td>
                  <td style={{ ...cellStyle, wordBreak: "break-all" }}>{col.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ ...labelStyle, marginBottom: "6px" }}>As CSV</p>
        <pre
          style={{ margin: "0 0 20px", padding: "12px", backgroundColor: "#0f172a", color: "#e2e8f0", borderRadius: "6px", fontSize: "12px", overflowX: "auto" }}
        >
          {buildTemplateCsv()}
        </pre>

        <h4 style={{ ...subheadingStyle, fontSize: "15px" }}>What happens after you upload</h4>
        <ol style={listStyle}>
          <li><strong>Every row is saved straight away.</strong></li>
          <li>
            <strong>Each company is prepared in the background.</strong> A missing website and "what they do" are
            looked up (never invented), and a missing LinkedIn page is filled in from a web search. Anything typed in
            is kept as typed.
          </li>
          <li>
            <strong>Wait for it to finish before searching.</strong> "Find companies" stays disabled while companies
            are still being prepared.
          </li>
          <li>
            <strong>Matching to applications.</strong> When "Find companies" runs, the list is matched to the chosen
            applications. Companies that fit appear first, before the organization's list and the company database.
            Companies that fit no application are listed under "didn't fit any application".
          </li>
        </ol>

        <h4 style={{ ...subheadingStyle, fontSize: "15px" }}>Uploading again</h4>
        <p style={{ ...mutedStyle, marginBottom: "12px" }}>
          Uploading again updates the same companies; it never duplicates them. Companies are matched by website, or by
          name when there is no website. Only new or changed companies are prepared again. After uploading again, run
          "Find companies" again so the new rows are matched to the applications.
        </p>

        <h4 style={{ ...subheadingStyle, fontSize: "15px" }}>Tips</h4>
        <ul style={{ ...listStyle, marginBottom: 0 }}>
          <li>Add a website if you know it. Companies without one can't be searched for people until one is found.</li>
          <li>A company with no website shows as "no website" and isn't sent on to the people search.</li>
        </ul>
      </section>
    </div>
  );
};

export default ClientFinder;
